import { format, formatDistanceToNow, isValid } from 'date-fns';

export {
  adaptiveDateFormat,
  pluralize,
  pluralizeWord,
  possessiveWord,
  timeDiff,
} from './utils/format-utils';

// Moment localized format equivalents in date-fns:
//   'lll'        → 'MMM d, yyyy h:mm a'  (e.g. "Sep 4, 1986 8:30 PM")
//   'll'         → 'MMM d, yyyy'         (e.g. "Sep 4, 1986")
//   'MMM D, YYYY'→ 'MMM d, yyyy'         (moment explicit token used in reports)
//
// Note: date-fns offers 'PPp' as a locale-aware equivalent of 'lll', but it produces
// a slightly different output (e.g. "Sep 4, 1986, 8:30 PM" — extra comma, locale-dependent
// ordering). We use an explicit format string here to preserve identical output to moment
// during migration. When i18n support is added, 'PPp' (with a locale passed to format())
// is the correct long-term replacement.
const MOMENT_FORMAT_MAP: Record<string, string> = {
  lll: 'MMM d, yyyy h:mm a',
  ll: 'MMM d, yyyy',
  'MMM D, YYYY': 'MMM d, yyyy',
};

type DateInput = string | number | Date | null | undefined;

/**
 * Note about formatting empty date values:
 * date value of 0 produces an epoch date
 * date value of null produces "Invalid Date"
 */
export const shortDateTimeFormat = (value: DateInput): string => {
  if (!value) return '';
  const date = new Date(value);
  return isValid(date) ? format(date, 'MMM d, h:mmaaa') : '';
};

/**
 * Format the date using the given format string.
 * Supports moment localized tokens ('ll', 'lll') mapped to their date-fns equivalents,
 * or any date-fns format string directly.
 * @param value - date string in a parsable format like iso8601
 * @param formatString - default is 'lll' which looks like 'Sep 4, 1986 8:30 PM'
 * example:
 * {{ targetDate | dateFormat('ll') }}
 */
export const dateFormat = (value: DateInput, formatString = 'lll'): string => {
  if (!value || value === 0) return '';
  const date = new Date(value);
  if (!isValid(date)) return '';
  return format(date, MOMENT_FORMAT_MAP[formatString] ?? formatString);
};

/**
 * Display time as difference from now, i.e. '1 minute ago'
 * example:
 * {{ job.completeTime | fromNow }}
 */
export const fromNow = (value: DateInput): string => {
  if (!value || value === 0) return '';
  const date = new Date(value);
  return isValid(date) ? formatDistanceToNow(date, { addSuffix: true }) : '';
};
